import { useState, useEffect } from 'react';
import { getDeliveries, getCustomers, getOrder, getCustomer, getSite, getSites } from '../services/api';
import { generateDeliveryChallan } from '../utils/deliveryChallan';
import { generatePostDeliveryChallan } from '../utils/postDeliveryChallan';
import { exportDeliveriesToExcel, exportDeliveriesByCustomer } from '../utils/exportDeliveries';
import CustomDropdown from './CustomDropdown';
import { useResponsive } from '../hooks/useResponsive';

function Deliveries() {
  const [deliveries, setDeliveries] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [selectedCustomer, setSelectedCustomer] = useState('all');
  const [isExporting, setIsExporting] = useState(false);

  const { isMobile } = useResponsive();

  useEffect(() => {
    loadDeliveries();
    loadCustomers();
    loadSites();
  }, []);

  const loadDeliveries = async () => {
    try {
      const data = await getDeliveries();
      setDeliveries(data);
    } catch (error) {
      alert('Error loading deliveries: ' + error.message);
    }
  };

  const loadCustomers = async () => {
    try {
      const data = await getCustomers();
      setCustomers(data);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const loadSites = async () => {
    try {
      const data = await getSites();
      setSites(data);
    } catch (error) {
      console.error('Error loading sites:', error);
    }
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer ? customer.name : 'Unknown';
  };

  const getSiteName = (siteId) => {
    const site = sites.find(s => s.id === siteId);
    return site ? (site.site_name || site.name) : null;
  };

  const getStatusBadge = (status) => {
    const colors = {
      delivered: '#27ae60',
      in_transit: '#f39c12',
      cancelled: '#e74c3c'
    };

    const displayStatus = status || 'in_transit';

    return (
      <span style={{
        padding: '4px 12px',
        background: colors[displayStatus] || '#95a5a6',
        color: 'white',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 'bold'
      }}>
        {displayStatus.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  const handleDownloadChallan = async (delivery) => {
    try {
      // Fetch order, customer, and site data
      const order = await getOrder(delivery.orderId);
      const customer = await getCustomer(delivery.customerId);

      // Fetch site if siteId exists
      let site = null;
      if (order.siteId) {
        try {
          site = await getSite(order.siteId);
        } catch (error) {
          console.error('Error fetching site:', error);
        }
      }

      // Merge delivery vehicle details into order (delivery data takes precedence)
      const vehicleNumber = delivery.vehicleNumber || order.vehicleNumber || '';
      const driverName = delivery.driverName || order.driverName || '';
      const driverPhone = delivery.driverPhone || order.driverPhone || '';

      const orderWithDeliveryDetails = {
        ...order,
        vehicleNumber,
        driverName,
        driverPhone,
        vehicle_number: vehicleNumber,
        driver_name: driverName,
        driver_phone: driverPhone,
        delivery_date: delivery.deliveryDate || order.deliveryDate,
        deliveryDate: delivery.deliveryDate || order.deliveryDate,
        delivery_challan_number: delivery.deliveryNumber || order.deliveryChallanNumber,
        deliveryChallanNumber: delivery.deliveryNumber || order.deliveryChallanNumber
      };

      // Generate and download the PDF
      generateDeliveryChallan(orderWithDeliveryDetails, customer, site);
    } catch (error) {
      alert('Error generating delivery challan: ' + error.message);
    }
  };

  const handleDownloadPostDeliveryChallan = async (delivery) => {
    try {
      // Fetch order, customer, and site data
      const order = await getOrder(delivery.orderId);
      const customer = await getCustomer(delivery.customerId);

      // Fetch site if siteId exists
      let site = null;
      if (order.siteId) {
        try {
          site = await getSite(order.siteId);
        } catch (error) {
          console.error('Error fetching site:', error);
        }
      }

      // Merge delivery vehicle details into order (delivery data takes precedence)
      const vehicleNumber = delivery.vehicleNumber || order.vehicleNumber || '';
      const driverName = delivery.driverName || order.driverName || '';
      const driverPhone = delivery.driverPhone || order.driverPhone || '';

      const orderWithDeliveryDetails = {
        ...order,
        vehicleNumber,
        driverName,
        driverPhone,
        vehicle_number: vehicleNumber,
        driver_name: driverName,
        driver_phone: driverPhone,
        delivery_date: delivery.deliveryDate || order.deliveryDate,
        deliveryDate: delivery.deliveryDate || order.deliveryDate,
        delivery_challan_number: delivery.deliveryNumber || order.deliveryChallanNumber,
        deliveryChallanNumber: delivery.deliveryNumber || order.deliveryChallanNumber
      };

      // Generate and download the PDF
      generatePostDeliveryChallan(orderWithDeliveryDetails, customer, site);
    } catch (error) {
      alert('Error generating post-delivery challan: ' + error.message);
    }
  };

  const getFilteredDeliveries = () => {
    let filtered = [...deliveries];

    // Customer filter - compare as strings (UUIDs)
    if (selectedCustomer && selectedCustomer !== 'all') {
      filtered = filtered.filter(delivery => delivery.customerId === selectedCustomer);
    }

    // Month filter
    if (selectedMonth && selectedMonth !== 'all') {
      filtered = filtered.filter(delivery => {
        const deliveryDate = new Date(delivery.deliveryDate);
        const [year, month] = selectedMonth.split('-');
        return deliveryDate.getFullYear() === parseInt(year) &&
               deliveryDate.getMonth() === parseInt(month) - 1;
      });
    }

    // Search filter
    if (searchTerm && searchTerm.trim()) {
      filtered = filtered.filter(delivery => {
        const customerName = getCustomerName(delivery.customerId).toLowerCase();
        const deliveryNumber = (delivery.deliveryNumber || '').toLowerCase();
        const vehicleNumber = (delivery.vehicleNumber || '').toLowerCase();
        const driverName = (delivery.driverName || '').toLowerCase();
        const searchLower = searchTerm.toLowerCase();

        return deliveryNumber.includes(searchLower) ||
               customerName.includes(searchLower) ||
               vehicleNumber.includes(searchLower) ||
               driverName.includes(searchLower);
      });
    }

    return filtered;
  };

  const filteredDeliveries = getFilteredDeliveries();

  const handleExportAll = async () => {
    if (filteredDeliveries.length === 0) {
      alert('No deliveries to export');
      return;
    }

    setIsExporting(true);
    try {
      // Generate filename based on filters
      let filename = 'Deliveries';
      if (selectedCustomer !== 'all') {
        const customer = customers.find(c => c.id === selectedCustomer);
        filename += `_${customer ? customer.name.replace(/\s+/g, '_') : 'Customer'}`;
      }
      if (selectedMonth !== 'all') {
        filename += `_${selectedMonth}`;
      }
      filename += `_${new Date().toISOString().split('T')[0]}.xlsx`;

      await exportDeliveriesToExcel(filteredDeliveries, customers, sites, filename);
      alert('Excel file downloaded successfully!');
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Error exporting to Excel: ' + error.message);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportByCustomer = async () => {
    if (selectedCustomer === 'all') {
      alert('Please select a customer to export');
      return;
    }

    setIsExporting(true);
    try {
      await exportDeliveriesByCustomer(deliveries, selectedCustomer, customers, sites);
      alert('Excel file downloaded successfully!');
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Error exporting to Excel: ' + error.message);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: isMobile ? 'wrap' : 'nowrap', gap: '10px' }}>
        <h1 style={{ color: '#3D1F1F', margin: 0 }}>Deliveries & Challans</h1>
        <button
          onClick={handleExportAll}
          disabled={isExporting}
          style={{
            padding: '10px 20px',
            background: isExporting ? '#95a5a6' : '#27ae60',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: isExporting ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            transition: 'all 0.2s',
            boxShadow: '0 2px 4px rgba(39, 174, 96, 0.3)',
            whiteSpace: 'nowrap',
            opacity: isExporting ? 0.7 : 1
          }}
          onMouseEnter={(e) => {
            if (!isExporting) {
              e.currentTarget.style.background = '#229954';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(39, 174, 96, 0.4)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isExporting) {
              e.currentTarget.style.background = '#27ae60';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(39, 174, 96, 0.3)';
            }
          }}
        >
          {isExporting ? 'Exporting...' : 'Export to Excel'}
        </button>
      </div>

      {/* Search and Filter Bar */}
      <div style={{
        background: 'white',
        padding: '15px',
        borderRadius: '8px',
        marginBottom: '20px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        display: 'flex',
        gap: '15px',
        alignItems: 'center',
        flexWrap: isMobile ? 'wrap' : 'nowrap'
      }}>
        <div style={{ flex: isMobile ? '1 1 100%' : 2 }}>
          <label style={{
            display: 'block',
            marginBottom: '5px',
            fontSize: '12px',
            fontWeight: '600',
            color: '#7f8c8d'
          }}>
            Search
          </label>
          <input
            type="text"
            placeholder="Search by delivery number, customer, vehicle, or driver..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              boxSizing: 'border-box'
            }}
          />
        </div>
        <div style={{ flex: isMobile ? '1 1 100%' : 1 }}>
          <label style={{
            display: 'block',
            marginBottom: '5px',
            fontSize: '12px',
            fontWeight: '600',
            color: '#7f8c8d'
          }}>
            Customer
          </label>
          <CustomDropdown
            value={selectedCustomer}
            onChange={(value) => setSelectedCustomer(value)}
            options={[
              { value: 'all', label: 'All Customers' },
              ...customers.map(customer => ({
                value: customer.id,
                label: customer.name
              }))
            ]}
            placeholder="All Customers"
          />
        </div>
        <div style={{ flex: isMobile ? '1 1 100%' : 1 }}>
          <label style={{
            display: 'block',
            marginBottom: '5px',
            fontSize: '12px',
            fontWeight: '600',
            color: '#7f8c8d'
          }}>
            Month
          </label>
          <CustomDropdown
            value={selectedMonth}
            onChange={(value) => setSelectedMonth(value)}
            options={[
              { value: 'all', label: 'All Months' },
              ...(() => {
                const months = [];
                const now = new Date();
                for (let i = 0; i < 12; i++) {
                  const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const label = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
                  months.push({ value: `${year}-${month}`, label });
                }
                return months;
              })()
            ]}
            placeholder="All Months"
          />
        </div>
      </div>

      {deliveries.length === 0 ? (
        <div style={{
          background: 'white',
          borderRadius: '8px',
          padding: '40px',
          textAlign: 'center',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <p style={{ color: '#7f8c8d', fontSize: '16px' }}>No deliveries found. Deliveries are created from confirmed orders.</p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: '20px'
        }}>
          {filteredDeliveries.map(delivery => (
            <div
              key={delivery.id}
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid #e9ecef',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px' }}>
                <div>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#3D1F1F', marginBottom: '4px' }}>
                    {delivery.deliveryNumber}
                  </div>
                  <div style={{ fontSize: '13px', color: '#7f8c8d' }}>
                    {new Date(delivery.deliveryDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </div>
                  {delivery.orderNumber && (
                    <div style={{ fontSize: '12px', color: '#8e44ad', marginTop: '4px', fontWeight: '500' }}>
                      Order: {delivery.orderNumber}
                    </div>
                  )}
                </div>
                {getStatusBadge(delivery.status)}
              </div>

              {/* Customer */}
              <div style={{
                background: '#f8f9fa',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '12px'
              }}>
                <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '4px' }}>Customer</div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#3D1F1F' }}>
                  {getCustomerName(delivery.customerId)}
                </div>
                {delivery.siteId && getSiteName(delivery.siteId) && (
                  <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '6px' }}>
                    Site: {getSiteName(delivery.siteId)}
                  </div>
                )}
              </div>

              {/* Vehicle & Driver Info */}
              <div style={{
                background: '#f8f9fa',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '16px'
              }}>
                <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '8px' }}>Vehicle & Driver</div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#3D1F1F', marginBottom: '6px' }}>
                  {delivery.vehicleNumber || 'N/A'}
                </div>
                {delivery.driverName && (
                  <div style={{ fontSize: '13px', color: '#555', marginBottom: '3px' }}>
                    <span style={{ color: '#7f8c8d' }}>Driver:</span> {delivery.driverName}
                  </div>
                )}
                {delivery.driverPhone && (
                  <div style={{ fontSize: '13px', color: '#555' }}>
                    <span style={{ color: '#7f8c8d' }}>Phone:</span> {delivery.driverPhone}
                  </div>
                )}
              </div>

              {/* Download Buttons */}
              <button
                onClick={() => handleDownloadChallan(delivery)}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  background: '#8e44ad',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'all 0.2s',
                  boxShadow: '0 2px 8px rgba(142, 68, 173, 0.3)',
                  marginBottom: delivery.status === 'delivered' ? '8px' : '0'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#7d3c98';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(142, 68, 173, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#8e44ad';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(142, 68, 173, 0.3)';
                }}
              >
                Download Delivery Challan
              </button>

              {delivery.status === 'delivered' && (
                <button
                  onClick={() => handleDownloadPostDeliveryChallan(delivery)}
                  style={{
                    width: '100%',
                    padding: '10px 16px',
                    background: '#27ae60',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'all 0.2s',
                    boxShadow: '0 2px 8px rgba(39, 174, 96, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#229954';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(39, 174, 96, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#27ae60';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(39, 174, 96, 0.3)';
                  }}
                >
                  Download Post-Delivery Challan
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Deliveries;
